/* eslint-disable react/no-array-index-key */
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Notifications from '../../../../../services/notifications';

// ─── CONFIG ─────────────────────────────────────────────────────────────────
const INITIAL_PVA = 0;
const INITIAL_LINES = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

// ─── DATA ───────────────────────────────────────────────────────────────────
const LEVELS = [
  { id: 1, name: 'Starter', minGVA: 0, minLines: 0, minLineVA: 0 },
  { id: 2, name: 'Liink Active', minGVA: 0, minLines: 0, minLineVA: 0 },
  { id: 3, name: 'Liink 1k', minGVA: 1000, minLines: 2, minLineVA: 200 },
  {
    id: 4,
    name: 'Liink 2500',
    minGVA: 2500,
    minLines: 2,
    minLineVA: 1000,
  },
  { id: 5, name: 'Liink 5k', minGVA: 5000, minLines: 2, minLineVA: 2000 },
  {
    id: 6,
    name: 'Liink 10k',
    minGVA: 10000,
    minLines: 2,
    minLineVA: 4000,
  },
  {
    id: 7,
    name: 'Liink 25k',
    minGVA: 25000,
    minLines: 2,
    minLineVA: 10000,
  },
  {
    id: 8,
    name: 'Liink 50k',
    minGVA: 50000,
    minLines: 2,
    minLineVA: 20000,
  },
  {
    id: 9,
    name: 'Liink 100k',
    minGVA: 100000,
    minLines: 2,
    minLineVA: 40000,
  },
  {
    id: 10,
    name: 'Liink 200k',
    minGVA: 200000,
    minLines: 2,
    minLineVA: 75000,
  },
  {
    id: 11,
    name: 'Liink 300k',
    minGVA: 300000,
    minLines: 2,
    minLineVA: 100000,
  },
  {
    id: 12,
    name: 'Liink 500k',
    minGVA: 500000,
    minLines: 2,
    minLineVA: 200000,
  },
];

const LEVEL_COLORS = [
  '#9ca3af',
  '#3d9da5',
  '#2d8a92',
  '#1f7a82',
  '#176a72',
  '#125c63',
  '#0E373B',
  '#0b2d30',
  '#071d1f',
  '#fbbf24',
  '#f59e0b',
  '#d97706',
];

const LINE_RATES = {
  2: { A: [5], B: [9], C: [10] },
  3: { A: [5, 2], B: [9, 2], C: [10, 3] },
  4: { A: [5, 4], B: [9, 4], C: [10, 5] },
  5: { A: [5, 4, 3], B: [9, 4, 3], C: [10, 5, 5] },
  6: { A: [5, 4, 3, 2], B: [9, 4, 3, 2], C: [10, 5, 5, 3] },
  7: { A: [5, 4, 3, 2, 2], B: [9, 4, 3, 2, 2], C: [10, 5, 5, 3, 3] },
  8: {
    A: [5, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    B: [9, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    C: [10, 5, 5, 3, 3, 1, 1, 1, 1, 1],
  },
  9: {
    A: [5, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    B: [9, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    C: [10, 5, 5, 3, 3, 1, 1, 1, 1, 1],
  },
  10: {
    A: [5, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    B: [9, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    C: [10, 5, 5, 3, 3, 1, 1, 1, 1, 1],
  },
  11: {
    A: [5, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    B: [9, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    C: [10, 5, 5, 3, 3, 1, 1, 1, 1, 1],
  },
  12: {
    A: [5, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    B: [9, 4, 3, 2, 2, 1, 1, 1, 1, 1],
    C: [10, 5, 5, 3, 3, 1, 1, 1, 1, 1],
  },
};

const CARD_THEMES = [
  {
    bg: 'linear-gradient(145deg, #f0fdf4 0%, #dcfce7 100%)',
    accent: '#16a34a',
    light: '#bbf7d0',
  },
  {
    bg: 'linear-gradient(145deg, #e8f5f6 0%, #d0edef 100%)',
    accent: '#0E373B',
    light: '#7ec8ce',
  },
  {
    bg: 'linear-gradient(145deg, #eef2ff 0%, #e0e7ff 100%)',
    accent: '#4f46e5',
    light: '#a5b4fc',
  },
  {
    bg: 'linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%)',
    accent: '#2563eb',
    light: '#93c5fd',
  },
  {
    bg: 'linear-gradient(145deg, #ecfeff 0%, #cffafe 100%)',
    accent: '#0891b2',
    light: '#67e8f9',
  },
  {
    bg: 'linear-gradient(145deg, #f0fdfa 0%, #ccfbf1 100%)',
    accent: '#0d9488',
    light: '#5eead4',
  },
  {
    bg: 'linear-gradient(145deg, #f0f9fa 0%, #dff0f1 100%)',
    accent: '#165a60',
    light: '#7ec8ce',
  },
  {
    bg: 'linear-gradient(145deg, #fdf2f8 0%, #fce7f3 100%)',
    accent: '#db2777',
    light: '#f9a8d4',
  },
  {
    bg: 'linear-gradient(145deg, #fff7ed 0%, #ffedd5 100%)',
    accent: '#ea580c',
    light: '#fdba74',
  },
  {
    bg: 'linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%)',
    accent: '#d97706',
    light: '#fcd34d',
  },
  {
    bg: 'linear-gradient(145deg, #fefce8 0%, #fef9c3 100%)',
    accent: '#ca8a04',
    light: '#fde047',
  },
  {
    bg: 'linear-gradient(145deg, #fef2f2 0%, #fecaca 100%)',
    accent: '#dc2626',
    light: '#fca5a5',
  },
];

// ─── HELPERS ────────────────────────────────────────────────────────────────

const getTier = pva => {
  if (pva < 500) return 'A';
  if (pva < 1000) return 'B';
  return 'C';
};

const getLineRates = (level, pva) => {
  if (level < 2) return [];
  const tier = getTier(pva);
  const rates = LINE_RATES[level]?.[tier] || [];
  const result = [];
  for (let i = 0; i < 10; i += 1) result.push(rates[i] || 0);
  return result;
};

const determineLevel = (pva, gva, lineVAs) => {
  if (pva < 250) return 1;
  for (let i = LEVELS.length - 1; i >= 2; i -= 1) {
    const lv = LEVELS[i];
    if (gva >= lv.minGVA) {
      const qualified = lineVAs.filter(v => v >= lv.minLineVA).length;
      if (qualified >= lv.minLines) return i + 1;
    }
  }
  return 2;
};

const getTrophyLevel = pva => {
  const thresholds = [5000, 4000, 3000, 2500, 2000, 1500, 1000, 500];
  for (let i = 0; i < thresholds.length; i += 1) {
    if (pva >= thresholds[i]) return 8 - i;
  }
  return 0;
};

const fmt = n => n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

const getTierLabel = (totalPVA, tier, t) => {
  if (totalPVA < 250) return t('pureliink.career.simulator.tierNoCommission');
  if (tier === 'A') return '250-499 VA';
  if (tier === 'B') return '500-999 VA';
  return '1000+ VA';
};

const getBarColor = (ok, isCurrent, accentColor) => {
  if (ok) return '#22c55e';
  if (isCurrent) return '#fff';
  return accentColor;
};

// ─── INLINE SVG ICONS ───────────────────────────────────────────────────────

function CheckIcon({ size, color, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

CheckIcon.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  style: PropTypes.shape({}),
};

CheckIcon.defaultProps = {
  size: 16,
  color: '#22c55e',
  style: {},
};

function XIcon({ size, color, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

XIcon.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  style: PropTypes.shape({}),
};

XIcon.defaultProps = {
  size: 16,
  color: '#d1d5db',
  style: {},
};

function InfoIcon({ size, color }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

InfoIcon.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
};

InfoIcon.defaultProps = {
  size: 16,
  color: '#9ca3af',
};

// ─── SMALL COMPONENTS ───────────────────────────────────────────────────────

function CircleProgress({ value, max, size, strokeWidth }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const done = pct >= 1;
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct);

  if (done) {
    return <CheckIcon size={size * 0.5} color="#22c55e" />;
  }

  const fontSize = Math.max(8, size * 0.24);
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#165a60"
          strokeWidth={strokeWidth}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'all 0.5s' }}
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          color: '#4b5563',
          fontSize,
        }}
      >
        {Math.round(pct * 100)}%
      </span>
    </div>
  );
}

CircleProgress.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
};

CircleProgress.defaultProps = {
  size: 28,
  strokeWidth: 3,
};

function Tip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <InfoIcon size={16} />
      </span>
      {show && (
        <span
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 8,
            background: '#111827',
            color: '#fff',
            fontSize: 12,
            borderRadius: 8,
            padding: '6px 12px',
            whiteSpace: 'nowrap',
            zIndex: 50,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

Tip.propTypes = {
  text: PropTypes.string.isRequired,
};

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  tip,
  suffix,
  color,
  marks,
  labelSize,
  inputWidth,
}) {
  const pct =
    max > min ? ((Math.min(value, max) - min) / (max - min)) * 100 : 0;
  const COLORS_MAP = {
    purple: '#0E373B',
    pink: '#d4a054',
    amber: '#f59e0b',
    green: '#10b981',
    cyan: '#06b6d4',
    violet: '#0E373B',
    indigo: '#0E373B',
  };
  const fillColor = COLORS_MAP[color] || COLORS_MAP.purple;
  const inputId = `slider-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label
            htmlFor={inputId}
            style={{
              fontSize: labelSize,
              fontWeight: 500,
              color: '#374151',
            }}
          >
            {label}
          </label>
          {tip && <Tip text={tip} />}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <input
            id={inputId}
            type="text"
            inputMode="decimal"
            value={value.toFixed(2)}
            onChange={e => {
              const raw = e.target.value.replace(/[^0-9.,\-]/g, '').replace(',', '.');
              const v = Number(raw) || 0;
              onChange(Math.max(min, Math.min(v, max * 10)));
            }}
            style={{
              width: inputWidth,
              textAlign: 'right',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 14,
              fontWeight: 600,
              outline: 'none',
            }}
          />
          <span style={{ fontSize: 12, color: '#6b7280' }}>{suffix}</span>
        </div>
      </div>
      <div style={{ position: 'relative', marginTop: marks ? 20 : 0 }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={Math.min(value, max)}
          onChange={e => onChange(Number(e.target.value))}
          className="pureliink-sim-range"
          style={{
            width: '100%',
            height: 8,
            borderRadius: 9999,
            appearance: 'none',
            WebkitAppearance: 'none',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 10,
            background: `linear-gradient(to right, ${fillColor} ${pct}%, #e5e7eb ${pct}%)`,
          }}
        />
        {marks && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 8,
              pointerEvents: 'none',
            }}
          >
            {marks.map(m => {
              const mPct = ((m.value - min) / (max - min)) * 100;
              const passed = value >= m.value;
              return (
                <div
                  key={m.value}
                  style={{
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    left: `${mPct}%`,
                    top: -1,
                    transform: 'translateX(-50%)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      marginBottom: 2,
                      color: passed ? '#0E373B' : '#3d9da5',
                      transform: 'translateY(-100%)',
                      marginTop: -2,
                    }}
                  >
                    {m.label}
                  </span>
                  <div
                    style={{
                      borderRadius: '50%',
                      width: passed ? 10 : 8,
                      height: passed ? 10 : 8,
                      backgroundColor: passed ? fillColor : '#fff',
                      border: passed ? 'none' : '1px solid #9ca3af',
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {!marks && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: '#9ca3af',
            marginTop: 2,
          }}
        >
          <span>{fmt(min)}</span>
          <span>{fmt(max)}</span>
        </div>
      )}
    </div>
  );
}

SliderInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  tip: PropTypes.string,
  suffix: PropTypes.string,
  color: PropTypes.string,
  marks: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  ),
  labelSize: PropTypes.number,
  inputWidth: PropTypes.number,
};

SliderInput.defaultProps = {
  min: 0,
  max: 10000,
  step: 50,
  tip: null,
  suffix: 'VA',
  color: 'purple',
  marks: null,
  labelSize: 12,
  inputWidth: 100,
};

function SimCard({
  title,
  subtitle,
  amount,
  amountColor,
  children,
  style: extraStyle,
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        ...extraStyle,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '12px 12px 8px',
        }}
      >
        <div>
          <h3
            style={{
              fontWeight: 600,
              color: '#1f2937',
              fontSize: 14,
              margin: 0,
            }}
          >
            {title}
          </h3>
          {subtitle && (
            <p
              style={{
                fontSize: 12,
                color: '#6b7280',
                marginTop: 2,
                marginBottom: 0,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
        {amount !== undefined && (
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: amountColor,
            }}
          >
            {fmt(amount)}&euro;
          </span>
        )}
      </div>
      {children && <div style={{ padding: '0 12px 12px' }}>{children}</div>}
    </div>
  );
}

SimCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  amount: PropTypes.number,
  amountColor: PropTypes.string,
  children: PropTypes.node,
  style: PropTypes.shape({}),
};

SimCard.defaultProps = {
  subtitle: null,
  amount: undefined,
  amountColor: '#0E373B',
  children: null,
  style: {},
};

// ─── SVG PIE CHART ──────────────────────────────────────────────────────────

function SimplePieChart({ data, size, innerRadius }) {
  const outerRadius = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  const total = data.reduce((a, b) => a + b.value, 0);
  if (total === 0) return null;

  let cumAngle = -Math.PI / 2;
  const slices = data.map(d => {
    const angle = (d.value / total) * 2 * Math.PI;
    const startAngle = cumAngle;
    const endAngle = cumAngle + angle;
    cumAngle = endAngle;

    const largeArc = angle > Math.PI ? 1 : 0;

    const outerX1 = cx + outerRadius * Math.cos(startAngle);
    const outerY1 = cy + outerRadius * Math.sin(startAngle);
    const outerX2 = cx + outerRadius * Math.cos(endAngle);
    const outerY2 = cy + outerRadius * Math.sin(endAngle);
    const innerX1 = cx + innerRadius * Math.cos(endAngle);
    const innerY1 = cy + innerRadius * Math.sin(endAngle);
    const innerX2 = cx + innerRadius * Math.cos(startAngle);
    const innerY2 = cy + innerRadius * Math.sin(startAngle);

    const path = [
      `M ${outerX1} ${outerY1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerX2} ${outerY2}`,
      `L ${innerX1} ${innerY1}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerX2} ${innerY2}`,
      'Z',
    ].join(' ');

    const labelAngle = startAngle + angle / 2;
    const labelR = (outerRadius + innerRadius) / 2;
    const labelX = cx + labelR * Math.cos(labelAngle);
    const labelY = cy + labelR * Math.sin(labelAngle);
    const pct = Math.round((d.value / total) * 100);

    return { ...d, path, labelX, labelY, pct };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map(s => (
        <g key={s.name}>
          <path d={s.path} fill={s.color} />
          {s.pct > 5 && (
            <text
              x={s.labelX}
              y={s.labelY}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#fff"
              fontSize="12"
              fontWeight="700"
            >
              {s.pct}%
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

SimplePieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string,
    }),
  ).isRequired,
  size: PropTypes.number,
  innerRadius: PropTypes.number,
};

SimplePieChart.defaultProps = {
  size: 180,
  innerRadius: 45,
};

// ─── LEVEL PROGRESSION SUB-COMPONENT ────────────────────────────────────────

function LevelProgression({
  t,
  level,
  totalPVA,
  gva,
  lines,
  tier,
  tierLabel,
  nextLevel,
  qualifiedLinesForNext,
}) {
  const scrollRef = useRef(null);
  const currentRef = useRef(null);

  useEffect(() => {
    if (currentRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const card = currentRef.current;
      const scrollLeft = card.offsetLeft - container.offsetLeft - 8;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }, [level]);

  const minShow = Math.max(0, level - 3);
  const visibleLevels = LEVELS.filter((_, idx) => idx >= minShow);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 12,
        alignItems: 'stretch',
      }}
    >
      {/* Left: level cards scroll */}
      <div
        style={{
          borderRadius: 12,
          padding: 12,
          background: '#fff',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <p
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#1f2937',
              margin: 0,
            }}
          >
            {t('pureliink.career.simulator.progression')}
          </p>
          <p
            style={{
              fontSize: 12,
              color: '#6b7280',
              margin: 0,
            }}
          >
            Tier {tier} &middot; {tierLabel}
          </p>
        </div>
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            gap: 12,
            overflowX: 'auto',
            paddingBottom: 8,
            scrollbarWidth: 'thin',
          }}
        >
          {visibleLevels.map(lv => {
            const idx = lv.id - 1;
            const lvNum = lv.id;
            const isCurrent = lvNum === level;
            const isPast = lvNum < level;
            const isFuture = lvNum > level;
            const qualLines =
              lv.minLines > 0 ? lines.filter(v => v >= lv.minLineVA).length : 0;
            const pvaOk = totalPVA >= 250;
            const gvaOk = lv.minGVA > 0 ? gva >= lv.minGVA : true;
            const linesOk = lv.minLines > 0 ? qualLines >= lv.minLines : true;
            const theme = CARD_THEMES[idx];
            const accentColor = isPast ? '#22c55e' : theme.accent;

            const reqRows = [];
            reqRows.push({
              label: 'PVA',
              ok: isPast || pvaOk,
              value: totalPVA,
              target: 250,
            });
            if (lv.minGVA > 0) {
              reqRows.push({
                label: 'GVA',
                ok: isPast || gvaOk,
                value: gva,
                target: lv.minGVA,
              });
            }
            if (lv.minLines > 0) {
              const topVAs = lines
                .filter(v => v > 0)
                .sort((a, b) => b - a)
                .slice(0, lv.minLines);
              const topLabel =
                topVAs.length > 0 ? topVAs.map(v => fmt(v)).join(' · ') : '';
              reqRows.push({
                label: t('pureliink.career.simulator.linesMin')
                  .replace('{count}', lv.minLines)
                  .replace('{value}', fmt(lv.minLineVA)),
                ok: isPast || linesOk,
                value: qualLines,
                target: lv.minLines,
                detail: topLabel,
              });
            }

            return (
              <div
                key={lv.id}
                ref={isCurrent ? currentRef : null}
                style={{
                  flexShrink: 0,
                  borderRadius: 16,
                  overflow: 'hidden',
                  width: 172,
                  background: isCurrent
                    ? `linear-gradient(145deg, ${accentColor}, ${theme.light})`
                    : theme.bg,
                  border: isCurrent
                    ? 'none'
                    : '1px solid rgba(255,255,255,0.5)',
                  opacity: isFuture ? 0.5 : 1,
                  boxShadow: isCurrent
                    ? '0 10px 25px rgba(0,0,0,0.15)'
                    : '0 1px 3px rgba(0,0,0,0.06)',
                  transform: isCurrent ? 'scale(1.05)' : 'none',
                  transition: 'all 0.3s',
                }}
              >
                <div
                  style={{
                    padding: '12px 12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 800,
                      flexShrink: 0,
                      backgroundColor: isCurrent
                        ? 'rgba(255,255,255,0.3)'
                        : accentColor,
                      color: '#fff',
                    }}
                  >
                    {lvNum}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isCurrent ? '#fff' : accentColor,
                        margin: 0,
                      }}
                    >
                      {lv.name}
                    </p>
                    {isCurrent && (
                      <p
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: 'rgba(255,255,255,0.8)',
                          margin: 0,
                        }}
                      >
                        {t('pureliink.career.simulator.currentLevel')}
                      </p>
                    )}
                    {isPast && (
                      <p
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: accentColor,
                          opacity: 0.7,
                          margin: 0,
                        }}
                      >
                        {t('pureliink.career.simulator.validated')}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ padding: '0 12px 12px' }}>
                  {reqRows.map(req => {
                    const rpct =
                      req.target > 0 ? Math.min(req.value / req.target, 1) : 0;
                    const textColor = isCurrent
                      ? 'rgba(255,255,255,0.9)'
                      : '#4b5563';
                    const subColor = isCurrent
                      ? 'rgba(255,255,255,0.7)'
                      : '#9ca3af';
                    const trackBg = isCurrent
                      ? 'rgba(255,255,255,0.2)'
                      : '#e5e7eb';
                    const barColor = getBarColor(
                      req.ok,
                      isCurrent,
                      accentColor,
                    );

                    return (
                      <div key={req.label} style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: textColor,
                            }}
                          >
                            {req.label}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: req.ok ? '#22c55e' : subColor,
                            }}
                          >
                            {req.ok
                              ? '✓'
                              : `${fmt(req.value)}/${fmt(req.target)}`}
                          </span>
                        </div>
                        <div
                          style={{
                            width: '100%',
                            borderRadius: 9999,
                            height: 6,
                            backgroundColor: trackBg,
                          }}
                        >
                          <div
                            style={{
                              height: 6,
                              borderRadius: 9999,
                              transition: 'all 0.5s',
                              width: `${rpct * 100}%`,
                              backgroundColor: barColor,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: next objective (fixed) */}
      <div style={{ position: 'sticky', top: 80, alignSelf: 'start' }}>
        {nextLevel ? (
          <div
            style={{
              borderRadius: 12,
              border: '1px solid #d0edef',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              padding: 12,
              background: 'linear-gradient(145deg, #f0f9fa 0%, #dff0f1 100%)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  flexShrink: 0,
                  background: '#0E373B',
                }}
              >
                {nextLevel.id}
              </div>
              <div>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#071d1f',
                    margin: 0,
                  }}
                >
                  {nextLevel.name}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: '#3d9da5',
                    margin: 0,
                  }}
                >
                  {t('pureliink.career.simulator.nextObjective')}
                </p>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  borderRadius: 8,
                  padding: 8,
                  background:
                    totalPVA >= 250 ? '#f0fdf4' : 'rgba(255,255,255,0.7)',
                  marginBottom: 8,
                }}
              >
                <CircleProgress
                  value={totalPVA}
                  max={250}
                  size={48}
                  strokeWidth={4}
                />
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      margin: 0,
                    }}
                  >
                    PVA ≥ 250
                  </p>
                  {totalPVA >= 250 ? (
                    <p style={{ fontSize: 12, color: '#16a34a', margin: 0 }}>
                      {fmt(totalPVA)} VA
                    </p>
                  ) : (
                    <p style={{ fontSize: 12, color: '#ef4444', margin: 0 }}>
                      -{fmt(250 - totalPVA)} VA
                    </p>
                  )}
                </div>
              </div>
              {nextLevel.minGVA > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    borderRadius: 8,
                    padding: 8,
                    background:
                      gva >= nextLevel.minGVA
                        ? '#f0fdf4'
                        : 'rgba(255,255,255,0.7)',
                    marginBottom: 8,
                  }}
                >
                  <CircleProgress
                    value={gva}
                    max={nextLevel.minGVA}
                    size={48}
                    strokeWidth={4}
                  />
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#374151',
                        margin: 0,
                      }}
                    >
                      GVA ≥ {fmt(nextLevel.minGVA)}
                    </p>
                    {gva >= nextLevel.minGVA ? (
                      <p style={{ fontSize: 12, color: '#16a34a', margin: 0 }}>
                        {fmt(gva)} VA
                      </p>
                    ) : (
                      <p style={{ fontSize: 12, color: '#ef4444', margin: 0 }}>
                        -{fmt(nextLevel.minGVA - gva)} VA
                      </p>
                    )}
                  </div>
                </div>
              )}
              {nextLevel.minLines > 0 &&
                (() => {
                  const allDone = qualifiedLinesForNext >= nextLevel.minLines;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        borderRadius: 8,
                        padding: 8,
                        background: allDone
                          ? '#f0fdf4'
                          : 'rgba(255,255,255,0.7)',
                      }}
                    >
                      <CircleProgress
                        value={qualifiedLinesForNext}
                        max={nextLevel.minLines}
                        size={48}
                        strokeWidth={4}
                      />
                      <div style={{ minWidth: 0 }}>
                        <p
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#374151',
                            margin: 0,
                          }}
                        >
                          {t('pureliink.career.simulator.linesMin')
                            .replace('{count}', nextLevel.minLines)
                            .replace('{value}', fmt(nextLevel.minLineVA))}
                        </p>
                        {allDone ? (
                          <p
                            style={{
                              fontSize: 12,
                              color: '#16a34a',
                              margin: 0,
                            }}
                          >
                            {t('pureliink.career.simulator.linesCount').replace(
                              '{count}',
                              qualifiedLinesForNext,
                            )}
                          </p>
                        ) : (
                          <p
                            style={{
                              fontSize: 12,
                              color: '#ef4444',
                              margin: 0,
                            }}
                          >
                            {(nextLevel.minLines - qualifiedLinesForNext > 1
                              ? t('pureliink.career.simulator.linesMissing')
                              : t('pureliink.career.simulator.lineMissing')
                            ).replace(
                              '{count}',
                              nextLevel.minLines - qualifiedLinesForNext,
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : (
          <div
            style={{
              borderRadius: 12,
              border: '1px solid #fcd34d',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: 16,
              background: 'linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%)',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              12
            </div>
            <p
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: '#92400e',
                marginTop: 4,
              }}
            >
              {t('pureliink.career.simulator.maxLevel')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const levelShapePropType = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  minGVA: PropTypes.number,
  minLines: PropTypes.number,
  minLineVA: PropTypes.number,
});

LevelProgression.propTypes = {
  t: PropTypes.func.isRequired,
  level: PropTypes.number.isRequired,
  totalPVA: PropTypes.number.isRequired,
  gva: PropTypes.number.isRequired,
  lines: PropTypes.arrayOf(PropTypes.number).isRequired,
  tier: PropTypes.string.isRequired,
  tierLabel: PropTypes.string.isRequired,
  nextLevel: levelShapePropType,
  qualifiedLinesForNext: PropTypes.number.isRequired,
};

LevelProgression.defaultProps = {
  nextLevel: null,
};

// ─── STATUS CELL HELPER ─────────────────────────────────────────────────────

function StatusCell({ t, isCurrent, canReach }) {
  if (isCurrent) {
    return (
      <span
        style={{
          display: 'inline-block',
          background: '#0E373B',
          color: '#fff',
          borderRadius: 9999,
          padding: '2px 6px',
          fontSize: 12,
        }}
      >
        {t('pureliink.career.simulator.currentLevel')}
      </span>
    );
  }
  if (canReach) {
    return <CheckIcon size={14} color="#22c55e" />;
  }
  return <XIcon size={14} color="#d1d5db" />;
}

StatusCell.propTypes = {
  t: PropTypes.func.isRequired,
  isCurrent: PropTypes.bool.isRequired,
  canReach: PropTypes.bool.isRequired,
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────

function CommissionSimulator({ t, userId, apiHeaders }) {
  const [totalPVA, setTotalPVA] = useState(INITIAL_PVA);
  const [lines, setLines] = useState(INITIAL_LINES);
  const [levelLines, setLevelLines] = useState([0, 0]);

  const fetchInitData = useCallback(() => {
    if (!userId) return;
    axios
      .get(`/api/tailor-made/pureliink/career/${userId}/simulator/init`, {
        headers: apiHeaders,
      })
      .then(({ data: response }) => {
        const initData = response.data;
        if (initData.currentPersonalVA != null) {
          setTotalPVA(initData.currentPersonalVA);
        }
        if (Array.isArray(initData.currentVAPerTier)) {
          const newLines = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
          initData.currentVAPerTier.forEach(entry => {
            const idx = entry.tier - 1;
            if (idx >= 0 && idx < 10) {
              newLines[idx] = entry.va || 0;
            }
          });
          setLines(newLines);
        }
        if (Array.isArray(initData.topLinesVA)) {
          setLevelLines([
            initData.topLinesVA[0] || 0,
            initData.topLinesVA[1] || 0,
          ]);
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_simulator_init');
      });
  }, [userId, apiHeaders]);

  useEffect(() => {
    fetchInitData();
  }, [fetchInitData]);

  // Derived values
  const teamVA = lines.reduce((a, b) => a + b, 0);
  const gva = totalPVA + teamVA;

  const level = useMemo(
    () => determineLevel(totalPVA, gva, levelLines),
    [totalPVA, gva, levelLines[0], levelLines[1]],
  );

  const tier = getTier(totalPVA);
  const tierLabel = getTierLabel(totalPVA, tier, t);

  // Personal commission
  const personalRate = totalPVA <= 500 ? 0.2 : 0.25;
  const personalCommission = totalPVA * personalRate;

  // Line commissions
  const lineRates = getLineRates(level, totalPVA);
  const lineCommissions = lines.map((v, i) => (v * (lineRates[i] || 0)) / 100);
  const totalLineCommission = lineCommissions.reduce((a, b) => a + b, 0);

  // Trophy
  const trophyLevel = getTrophyLevel(totalPVA);

  // Totals
  const totalMonthly = personalCommission + totalLineCommission;

  // Next level info
  const nextLevel = level < 12 ? LEVELS[level] : null;
  const qualifiedLinesForNext = nextLevel
    ? levelLines.filter(v => v >= nextLevel.minLineVA).length
    : 0;

  // Pie chart data
  const pieData = [
    {
      name: t('pureliink.career.simulator.personalCommissionPie'),
      value: personalCommission,
      color: '#165a60',
    },
    {
      name: t('pureliink.career.simulator.lineCommissionsPie'),
      value: totalLineCommission,
      color: '#d4a054',
    },
  ].filter(d => d.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header — sticky summary */}
      <div
        style={{
          background: 'linear-gradient(to right, #0E373B, #165a60)',
          borderRadius: 12,
          padding: 16,
          color: '#fff',
          boxShadow: '0 10px 25px rgba(14,55,59,0.3)',
          position: 'sticky',
          top: 4,
          zIndex: 30,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: '-0.025em',
                margin: 0,
              }}
            >
              {t('pureliink.career.simulator.header')}
            </h1>
            <p
              style={{
                color: '#7ec8ce',
                fontSize: 12,
                margin: 0,
              }}
            >
              {t('pureliink.career.simulator.headerSubtitle')}
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: '#7ec8ce', margin: 0 }}>
                {t('pureliink.career.simulator.levelLabel')}
              </p>
              <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {LEVELS[level - 1].name}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: '#7ec8ce', margin: 0 }}>PVA</p>
              <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {fmt(totalPVA)}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: '#7ec8ce', margin: 0 }}>
                {t('pureliink.career.simulator.revenueLabel')}
              </p>
              <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {fmt(totalMonthly)}&euro;
              </p>
            </div>
            {trophyLevel > 0 && (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#7ec8ce', margin: 0 }}>
                  {t('pureliink.career.simulator.trophyLabel')}
                </p>
                <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                  {trophyLevel}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PVA + Commission perso side by side */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 12,
        }}
      >
        <SimCard
          title={t('pureliink.career.simulator.pvaTitle')}
          subtitle={t('pureliink.career.simulator.pvaSubtitle')}
        >
          <SliderInput
            label={t('pureliink.career.simulator.pvaSliderLabel')}
            value={totalPVA}
            onChange={setTotalPVA}
            max={1000}
            step={10}
            tip={t('pureliink.career.simulator.pvaSliderTip')}
            marks={[
              { value: 250, label: '250' },
              { value: 500, label: '500' },
              { value: 1000, label: '1000+' },
            ]}
          />
        </SimCard>

        <div
          style={{
            borderRadius: 12,
            border: '1px solid #7ec8ce',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: 16,
            background: 'linear-gradient(145deg, #e8f5f6 0%, #d0edef 100%)',
          }}
        >
          <p
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: '#165a60',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0,
            }}
          >
            {t('pureliink.career.simulator.personalCommission')}
          </p>
          <p
            style={{
              fontSize: 30,
              fontWeight: 800,
              color: '#0E373B',
              marginTop: 4,
              marginBottom: 0,
            }}
          >
            {fmt(personalCommission)}&euro;
          </p>
          <div
            style={{
              marginTop: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(255,255,255,0.6)',
              borderRadius: 9999,
              padding: '4px 12px',
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#0E373B',
              }}
            >
              {(personalRate * 100).toFixed(0)}%
            </span>
            <span style={{ fontSize: 12, color: '#3d9da5' }}>
              {t('pureliink.career.simulator.onVA').replace(
                '{va}',
                fmt(totalPVA),
              )}
            </span>
          </div>
          <p
            style={{
              fontSize: 12,
              color: '#3d9da5',
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            {t('pureliink.career.simulator.personalCommissionRule')}
          </p>
        </div>
      </div>

      {/* Levels + Next objective */}
      <LevelProgression
        t={t}
        level={level}
        totalPVA={totalPVA}
        gva={gva}
        lines={levelLines}
        tier={tier}
        tierLabel={tierLabel}
        nextLevel={nextLevel}
        qualifiedLinesForNext={qualifiedLinesForNext}
      />

      {/* Line VA sliders for level calculation */}
      <SimCard
        title={t('pureliink.career.simulator.levelLinesTitle')}
        subtitle={t('pureliink.career.simulator.levelLinesSubtitle')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[0, 1].map(idx => {
            const val = levelLines[idx];
            const pct = Math.min(val / 250000, 1) * 100;
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    whiteSpace: 'nowrap',
                    width: 44,
                    flexShrink: 0,
                  }}
                >
                  {t('pureliink.career.simulator.levelLine')} {idx + 1}
                </span>
                <input
                  type="range"
                  min={0}
                  max={250000}
                  step={500}
                  value={Math.min(val, 250000)}
                  onChange={e => {
                    const n = [...levelLines];
                    n[idx] = Number(e.target.value);
                    setLevelLines(n);
                  }}
                  className="pureliink-sim-range"
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 9999,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer',
                    background: `linear-gradient(to right, #0E373B ${pct}%, #e5e7eb ${pct}%)`,
                    minWidth: 0,
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={val.toFixed(2)}
                  onChange={e => {
                    const raw = e.target.value.replace(/[^0-9.,\-]/g, '').replace(',', '.');
                    const n = [...levelLines];
                    n[idx] = Math.max(0, Number(raw) || 0);
                    setLevelLines(n);
                  }}
                  style={{
                    width: 100,
                    textAlign: 'right',
                    border: '1px solid #e5e7eb',
                    borderRadius: 4,
                    padding: '2px 4px',
                    fontSize: 12,
                    fontWeight: 600,
                    outline: 'none',
                  }}
                />
                <span style={{ fontSize: 12, color: '#6b7280' }}>VA</span>
              </div>
            );
          })}
        </div>
      </SimCard>

      {/* Team Lines */}
      <SimCard
        title={t('pureliink.career.simulator.teamTitle')}
        subtitle={t('pureliink.career.simulator.teamSubtitle')
          .replace('{pva}', fmt(totalPVA))
          .replace('{team}', fmt(teamVA))
          .replace('{gva}', fmt(gva))}
        amount={totalLineCommission}
        amountColor="#d4a054"
      >
        <div>
          {lines.map((lineVA, idx) => {
            const rate = lineRates[idx] || 0;
            const comm = lineCommissions[idx];
            const nextReq = nextLevel?.minLineVA || 0;
            const qualifiedForNext = nextReq > 0 && lineVA >= nextReq;
            const sliderPct = Math.min(lineVA / 250000, 1) * 100;
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 6px',
                  background: '#f9fafb',
                  borderRadius: 8,
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#d0edef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#0E373B',
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>
                <input
                  type="range"
                  min={0}
                  max={250000}
                  step={100}
                  value={Math.min(lineVA, 250000)}
                  onChange={e => {
                    const n = [...lines];
                    n[idx] = Number(e.target.value);
                    setLines(n);
                  }}
                  className="pureliink-sim-range"
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 9999,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer',
                    background: `linear-gradient(to right, #0E373B ${sliderPct}%, #e5e7eb ${sliderPct}%)`,
                    minWidth: 0,
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={lineVA.toFixed(2)}
                  onChange={e => {
                    const raw = e.target.value.replace(/[^0-9.,\-]/g, '').replace(',', '.');
                    const n = [...lines];
                    n[idx] = Math.max(0, Number(raw) || 0);
                    setLines(n);
                  }}
                  style={{
                    width: 100,
                    textAlign: 'right',
                    border: '1px solid #e5e7eb',
                    borderRadius: 4,
                    padding: '2px 4px',
                    fontSize: 12,
                    fontWeight: 600,
                    outline: 'none',
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#0E373B',
                    width: 28,
                    textAlign: 'right',
                    flexShrink: 0,
                  }}
                >
                  {rate}%
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#16a34a',
                    width: 48,
                    textAlign: 'right',
                    flexShrink: 0,
                  }}
                >
                  {fmt(comm)}&euro;
                </span>
                {nextReq > 0 &&
                  (qualifiedForNext ? (
                    <CheckIcon size={14} color="#22c55e" />
                  ) : (
                    <XIcon size={14} color="#d1d5db" />
                  ))}
              </div>
            );
          })}
        </div>
      </SimCard>

      {/* Levels table */}
      <SimCard
        title={t('pureliink.career.simulator.levelsTableTitle')}
        subtitle={t('pureliink.career.simulator.levelsTableSubtitle')
          .replace('{tier}', tier)
          .replace('{tierLabel}', tierLabel)}
      >
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              fontSize: 12,
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: '1px solid #e5e7eb',
                  color: '#6b7280',
                }}
              >
                {[
                  t('pureliink.career.simulator.colLevel'),
                  t('pureliink.career.simulator.colGvaMin'),
                  t('pureliink.career.simulator.colLines'),
                  t('pureliink.career.simulator.colVaPerLine'),
                  'T1',
                  'T2',
                  'T3',
                  'T4',
                  'T5',
                  'T6-10',
                  t('pureliink.career.simulator.colStatus'),
                ].map((header, hIdx) => {
                  let align = 'center';
                  if (hIdx === 0) align = 'left';
                  else if (hIdx <= 3) align = 'right';
                  return (
                    <th
                      key={header}
                      style={{
                        textAlign: align,
                        padding: '6px',
                        fontWeight: 600,
                      }}
                    >
                      {header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {LEVELS.map((lv, idx) => {
                const isCurrent = idx + 1 === level;
                const rates = getLineRates(idx + 1, totalPVA);
                const canReach =
                  totalPVA >= 250 &&
                  (idx < 2 ||
                    (gva >= lv.minGVA &&
                      levelLines.filter(v => v >= lv.minLineVA).length >=
                        lv.minLines));
                return (
                  <tr
                    key={lv.id}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      background: isCurrent ? '#e8f5f6' : 'transparent',
                      fontWeight: isCurrent ? 600 : 400,
                    }}
                  >
                    <td
                      style={{
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: LEVEL_COLORS[idx],
                        }}
                      >
                        {idx + 1}
                      </div>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {lv.name}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px' }}>
                      {lv.minGVA > 0 ? fmt(lv.minGVA) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px' }}>
                      {lv.minLines > 0 ? lv.minLines : '—'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px' }}>
                      {lv.minLineVA > 0 ? fmt(lv.minLineVA) : '—'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      {rates[0] || '—'}
                      {rates[0] ? '%' : ''}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      {rates[1] || '—'}
                      {rates[1] ? '%' : ''}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      {rates[2] || '—'}
                      {rates[2] ? '%' : ''}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      {rates[3] || '—'}
                      {rates[3] ? '%' : ''}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      {rates[4] || '—'}
                      {rates[4] ? '%' : ''}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      {rates[5] ? `${rates[5]}%` : '—'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>
                      <StatusCell
                        t={t}
                        isCurrent={isCurrent}
                        canReach={canReach}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SimCard>

      {/* Pie + Breakdown */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}
      >
        {pieData.length > 0 && (
          <SimCard title={t('pureliink.career.simulator.distribution')}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <SimplePieChart data={pieData} size={200} innerRadius={50} />
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                justifyContent: 'center',
                marginTop: 8,
              }}
            >
              {pieData.map(d => (
                <div
                  key={d.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: d.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: '#4b5563',
                    }}
                  >
                    {d.name}
                  </span>
                </div>
              ))}
            </div>
          </SimCard>
        )}
        <SimCard title={t('pureliink.career.simulator.breakdown')}>
          <div>
            {[
              {
                label: t('pureliink.career.simulator.personalCommissionPie'),
                value: personalCommission,
                detail: `${fmt(totalPVA)} VA × ${(personalRate * 100).toFixed(
                  0,
                )}%`,
              },
              {
                label: t('pureliink.career.simulator.lineCommissionsPie'),
                value: totalLineCommission,
                detail: t('pureliink.career.simulator.activeLines').replace(
                  '{count}',
                  lines.filter(v => v > 0).length,
                ),
              },
            ].map(row => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  borderRadius: 4,
                  marginBottom: 6,
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#374151',
                      margin: 0,
                    }}
                  >
                    {row.label}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: '#9ca3af',
                      margin: 0,
                    }}
                  >
                    {row.detail}
                  </p>
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: row.value > 0 ? '#16a34a' : '#9ca3af',
                  }}
                >
                  {row.value > 0 ? `${fmt(row.value)}€` : '—'}
                </span>
              </div>
            ))}
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                paddingTop: 6,
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                {t('pureliink.career.simulator.total')}
              </span>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#0E373B',
                }}
              >
                {fmt(totalMonthly)}&euro;
              </span>
            </div>
          </div>
        </SimCard>
      </div>

      {/* Footer */}
      <p
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: '#9ca3af',
          paddingBottom: 16,
        }}
      >
        {t('pureliink.career.simulator.footer')}
      </p>
    </div>
  );
}

CommissionSimulator.propTypes = {
  t: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  apiHeaders: PropTypes.shape({}).isRequired,
};

export default CommissionSimulator;
